import {ICompareValueItem, IConditionFieldPreset} from "../../../policy-types";
import type {ILocationToken} from "./location-selection-types";
import LocationSemanticGroupConverter from "./location-semantic-group-converter";
import {
    CHECKBOX_TREE_COUNT_MODE,
    ICheckboxesTreeItem
} from "../../../../../components/inputs/checkboxes-tree-dropdown/checkboxes-tree/stores/checkboxes-tree-store";

/** Node types for location tree structure. */
enum LOCATION_NODE_TYPE {
    GROUP = 'group',
    COUNTRY = 'country',
    REGION = 'region',
    CITY = 'city'
}

/**
 * LocationTreeConverter (stateless utility)
 * - Builds static options tree from dataset
 * - Converts tokens to tree structure (pure function)
 * - Extracts tokens from tree selection (pure function)
 * - Provides O(1) lookup methods to enrich tokens with human-readable names
 *
 * Semantic group membership and token hydration belong to LocationSemanticGroupConverter;
 * this converter only needs to know which dataset codes are groups.
 */
class LocationTreeConverter {
    private readonly _optionsTree: ICheckboxesTreeItem[] = [];

    /**
     * Lookup map for O(1) access to location names by code.
     * Keys: "c:US", "p:US:CA", "ct:US:NYC".
     */
    private readonly _lookupMap: Map<string, string> = new Map();

    /** Initializes converter, builds options tree and lookup map from field preset. */
    constructor(
        fieldPreset: IConditionFieldPreset,
        private readonly _semanticGroupConverter: LocationSemanticGroupConverter
    ) {
        const dataset = fieldPreset.compare_value_data as ICompareValueItem[];
        this._lookupMap = this._buildLookupMap(dataset);
        this._optionsTree = this._buildOptionsTree(dataset);
    }

    /** Returns country name by code. O(1) lookup. Falls back to code if not found. */
    public getCountryName(countryCode: string): string {
        return this._lookupMap.get(`c:${countryCode}`) || countryCode;
    }

    /** Returns province name by country and province code. O(1) lookup. */
    public getProvinceName(countryCode: string, provinceCode: string): string | undefined {
        return this._lookupMap.get(`p:${countryCode}:${provinceCode}`);
    }

    /** Returns city name by country and city code. O(1) lookup. */
    public getCityName(countryCode: string, cityCode: string): string | undefined {
        return this._lookupMap.get(`ct:${countryCode}:${cityCode}`);
    }

    /** Builds and returns lookup map for O(1) name resolution. Pure function. */
    private _buildLookupMap(dataset: ICompareValueItem[]): Map<string, string> {
        const lookupMap = new Map<string, string>();

        for (const item of dataset) {
            this._indexCompareValueItem(
                lookupMap,
                item,
                this._semanticGroupConverter.isSemanticGroupCode(item.code ?? '')
            );
        }

        return lookupMap;
    }

    /** Returns the full options tree. */
    public getOptionsTree(): ICheckboxesTreeItem[] {
        return this._optionsTree;
    }

    /** Builds the selected tree for CheckboxesTreeDropdown from hydrated UI tokens. */
    public getValueTree(tokens: ILocationToken[]): ICheckboxesTreeItem[] {
        if (!tokens.length) {
            return [];
        }

        const selectedRoots: ICheckboxesTreeItem[] = [];

        for (const token of tokens) {
            this._addTokenToTree(selectedRoots, token);
        }

        return selectedRoots;
    }

    /**
     * Extracts location tokens from tree selection (compressed mode).
     * Preserves distinction: undefined = no selection, [] = select all, [nodes] = specific selection.
     */
    public extractTokensFromTree(items: ICheckboxesTreeItem[] | undefined): ILocationToken[] | undefined {
        if (!items) {
            return undefined;
        }

        if (items.length === 0) {
            return [];
        }

        const result: ILocationToken[] = [];
        const traverse = (nodes: ICheckboxesTreeItem[], path: ICheckboxesTreeItem[] = []) => {
            for (const node of nodes) {
                const currentPath = [...path, node];
                const children = node.childNodes;

                /** Leaf node - extract token from full path */
                if (!children?.length) {
                    const token = this._buildTokenFromPath(currentPath);
                    if (token) {
                        result.push(token);
                    }
                    continue;
                }

                /** Branch node - continue traversing */
                traverse(children, currentPath);
            }
        };
        traverse(items);

        return result;
    }

    /** Recursively indexes country, province, and city names for O(1) lookup. */
    private _indexCompareValueItem(
        lookupMap: Map<string, string>,
        item: ICompareValueItem,
        isSemanticGroup: boolean
    ): void {
        if (!item.code) {
            return;
        }

        const itemCode = item.code;

        /** Group labels are owned by LocationSemanticGroupConverter; only member countries are indexed here. */
        if (isSemanticGroup) {
            item.countries?.forEach((country) => {
                this._indexCompareValueItem(lookupMap, country, false);
            });
            return;
        }

        lookupMap.set(`c:${itemCode}`, item.name || itemCode);

        item.provinces?.forEach(province => {
            if (province.code) {
                lookupMap.set(`p:${itemCode}:${province.code}`, province.name || province.code);
            }
        });

        item.cities?.forEach(city => {
            if (city.code) {
                lookupMap.set(`ct:${itemCode}:${city.code}`, city.name || city.code);
            }
        });
    }

    /** Builds location token from full path of tree nodes based on node types. */
    private _buildTokenFromPath(path: ICheckboxesTreeItem[]): ILocationToken | null {
        if (path.length === 0) {
            return null;
        }

        let groupCode: string | undefined;
        let countryCode: string | undefined;
        let provinceCode: string | undefined;
        let cityCode: string | undefined;

        for (const node of path) {
            if (node.type === LOCATION_NODE_TYPE.GROUP) {
                groupCode = node.code || node.value;
            }
            else if (node.type === LOCATION_NODE_TYPE.COUNTRY) {
                countryCode = node.code || node.value;
            }
            else if (node.type === LOCATION_NODE_TYPE.REGION) {
                provinceCode = node.code || node.value;
            }
            else if (node.type === LOCATION_NODE_TYPE.CITY) {
                cityCode = node.code || node.value;
            }
        }

        if (groupCode && !countryCode && !provinceCode && !cityCode) {
            return {group: groupCode};
        }

        if (!countryCode) {
            return null;
        }

        const token: ILocationToken = {
            group: groupCode ?? null,
            country: countryCode,
        };

        if (provinceCode) {
            token.province = provinceCode;
        }

        if (cityCode) {
            token.city = cityCode;
        }

        return token;
    }

    /** Type guard: validates if item has valid code. */
    private _hasValidCode(item: unknown): item is { code: string; name?: string } {
        return typeof item === 'object' && item !== null &&
               'code' in item && typeof (item as any).code === 'string' && !!(item as any).code;
    }

    /** Type guard: validates if city has province code. */
    private _hasProvinceCode(city: unknown): city is { provinceCode: string } {
        return typeof city === 'object' && city !== null &&
               'provinceCode' in city && typeof (city as { provinceCode: string }).provinceCode === 'string';
    }

    /** Creates a tree node with optional children. */
    private _createTreeNode(
        value: string,
        label: string | undefined,
        code: string,
        type: LOCATION_NODE_TYPE,
        children?: ICheckboxesTreeItem[],
        countMode?: CHECKBOX_TREE_COUNT_MODE
    ): ICheckboxesTreeItem {
        return {
            value,
            label: label ?? value,
            code,
            type,
            disabled: false,
            ...(countMode ? {countMode} : {}),
            ...(children?.length ? {childNodes: children} : {})
        };
    }

    /** Builds full options tree from dataset. Groups cities by province. */
    private _buildOptionsTree(compareValueItems: ICompareValueItem[]): ICheckboxesTreeItem[] {
        if (!compareValueItems.length) {
            return [];
        }

        return compareValueItems
            .filter((item) => this._hasValidCode(item))
            .map((item) => this._buildLocationNode(
                item,
                this._semanticGroupConverter.isSemanticGroupCode(item.code!)
            ));
    }

    /** Builds a semantic group or country node and its nested children. */
    private _buildLocationNode(item: ICompareValueItem, isSemanticGroup: boolean): ICheckboxesTreeItem {
        const itemCode = item.code!;
        const childNodes = isSemanticGroup
            ? (item.countries ?? [])
                .filter((country) => this._hasValidCode(country))
                .map((country) => this._buildLocationNode(country, false))
            : this._buildProvinceAndCityNodes(item);

        /** Groups summarize member countries, so a member with nested regions still counts as one unit. */
        return this._createTreeNode(
            itemCode,
            item.name,
            itemCode,
            isSemanticGroup ? LOCATION_NODE_TYPE.GROUP : LOCATION_NODE_TYPE.COUNTRY,
            childNodes,
            isSemanticGroup ? CHECKBOX_TREE_COUNT_MODE.DIRECT_CHILDREN : undefined
        );
    }

    /** Builds province and city nodes for a regular country item. */
    private _buildProvinceAndCityNodes(country: ICompareValueItem): ICheckboxesTreeItem[] {
        const provinceNodes: ICheckboxesTreeItem[] = [];
        const cityNodes: ICheckboxesTreeItem[] = [];

        /** Group cities by province */
        const citiesByProvince = new Map<string, typeof country.cities>();
        const citiesWithoutProvince: typeof country.cities = [];

        (country.cities ?? []).forEach((city) => {
            if (!this._hasValidCode(city)) {
                return;
            }
            if (this._hasProvinceCode(city)) {
                const provinceCode = city.provinceCode!;
                if (!citiesByProvince.has(provinceCode)) {
                    citiesByProvince.set(provinceCode, []);
                }
                citiesByProvince.get(provinceCode)!.push(city);
            }
            else {
                citiesWithoutProvince.push(city);
            }
        });

        /** Build province nodes with their cities */
        (country.provinces ?? [])
            .filter((province) => this._hasValidCode(province))
            .forEach((province) => {
                const provinceCode = province.code!;
                const provinceCities = citiesByProvince.get(provinceCode) ?? [];

                const cityChildren = provinceCities
                    .map((city) => this._createTreeNode(
                        city.code!,
                        city.name,
                        city.code!,
                        LOCATION_NODE_TYPE.CITY
                    ));

                provinceNodes.push(
                    this._createTreeNode(
                        provinceCode,
                        province.name,
                        provinceCode,
                        LOCATION_NODE_TYPE.REGION,
                        cityChildren
                    )
                );
            });

        /** Build city nodes without province */
        citiesWithoutProvince
            .forEach((city) => {
                cityNodes.push(
                    this._createTreeNode(
                        city.code!,
                        city.name,
                        city.code!,
                        LOCATION_NODE_TYPE.CITY
                    )
                );
            });

        return [...provinceNodes, ...cityNodes];
    }

    /** Finds existing node or creates new one in parent array. */
    private _findOrCreateNode(
        parent: ICheckboxesTreeItem[],
        value: string,
        type: LOCATION_NODE_TYPE
    ): ICheckboxesTreeItem {
        let node = parent.find(n => n.value === value);
        if (!node) {
            node = this._createTreeNode(value, value, value, type);
            parent.push(node);
        }
        return node;
    }

    /** Ensures node has childNodes array initialized. */
    private _ensureChildNodes(node: ICheckboxesTreeItem): ICheckboxesTreeItem[] {
        if (!node.childNodes) {
            node.childNodes = [];
        }
        return node.childNodes;
    }

    /** Adds token to tree by finding or creating nodes. */
    private _addTokenToTree(roots: ICheckboxesTreeItem[], token: ILocationToken): void {
        if (token.group && !token.country) {
            this._findOrCreateNode(roots, token.group, LOCATION_NODE_TYPE.GROUP);
            return;
        }

        if (!token.country) {
            return;
        }

        let parentChildren = roots;

        if (token.group) {
            const groupNode = this._findOrCreateNode(
                parentChildren,
                token.group,
                LOCATION_NODE_TYPE.GROUP
            );
            parentChildren = this._ensureChildNodes(groupNode);
        }

        const countryNode = this._findOrCreateNode(
            parentChildren,
            token.country,
            LOCATION_NODE_TYPE.COUNTRY
        );

        if (!token.province && !token.city) {
            return;
        }

        const countryChildren = this._ensureChildNodes(countryNode);

        if (token.province) {
            const provinceNode = this._findOrCreateNode(
                countryChildren,
                token.province,
                LOCATION_NODE_TYPE.REGION
            );

            if (token.city) {
                const provinceChildren = this._ensureChildNodes(provinceNode);
                this._findOrCreateNode(
                    provinceChildren,
                    token.city,
                    LOCATION_NODE_TYPE.CITY
                );
            }
        }
        else if (token.city) {
            this._findOrCreateNode(
                countryChildren,
                token.city,
                LOCATION_NODE_TYPE.CITY
            );
        }
    }
}

export default LocationTreeConverter;
